import React, { useCallback, type FC } from 'react'
import { useIntl } from 'react-intl'
import { useAtom } from 'jotai'
import styled from '@emotion/styled'
import {
  Actions,
  DataListRow,
  Grid,
  GRID_VARIANT_DEFAULT,
  GridCell,
  Link,
  LINK_VARIANT_HIGHLIGHT,
  SkeletonLoader,
  SKELETON_LOADER_VARIANT_ONWHITE,
} from 'mailup-web-components'
import { type Category } from '../../../types/beFiltersApi'
import { pageState } from '../../../state/page'
import { COL_SPAN } from '../helpers/constants'
import { useAtomCallback } from 'jotai/utils'
import { openCategoryFormCallback } from '../callbacks/form'
import { openModalDeleteCategoryCallback } from '../callbacks/modal'

interface CategoryRowProps {
  category: Category
}

const StyledLink = styled(Link)`
  word-break: break-all;
`

export const CategoryRow: FC<CategoryRowProps> = ({ category }) => {
  const intl = useIntl()
  const [{ status }] = useAtom(pageState)
  const handleDelete = useAtomCallback(
    useCallback(
      (get, set) => {
        openModalDeleteCategoryCallback(get, set, category.Id)
      },
      [category],
    ),
  )
  const handleEdit = useAtomCallback(
    useCallback(
      (get, set) => {
        openCategoryFormCallback(get, set, category)
      },
      [category],
    ),
  )

  const labelEdit = intl.formatMessage({ id: 'Load.MailupCommon.Edit' })
  const labelDelete = intl.formatMessage({ id: 'Load.MailupCommon.Delete' })

  const actions = [
    {
      label: labelEdit,
      value: category,
      onClick: handleEdit,
    },
    {
      label: labelDelete,
      onClick: handleDelete,
      value: category.Id,
    },
  ]

  return (
    <DataListRow
      key={category.Id}
      data-mu-test={`category-table-row_${category.Id}`}
    >
      <Grid cols={12} variant={GRID_VARIANT_DEFAULT}>
        {status !== 'fetching' ? (
          <>
            <GridCell span={COL_SPAN.ID}>{category.Id}</GridCell>
            <GridCell span={COL_SPAN.NAME}>
              <StyledLink
                onClick={() => {
                  handleEdit()
                }}
                variant={LINK_VARIANT_HIGHLIGHT}
              >
                {category.Name}
              </StyledLink>
            </GridCell>
            <GridCell span={COL_SPAN.ACTIONS}>
              <Actions items={actions} />
            </GridCell>
          </>
        ) : (
          <>
            <GridCell span={COL_SPAN.ID}>
              <SkeletonLoader variant={SKELETON_LOADER_VARIANT_ONWHITE} />
            </GridCell>
            <GridCell span={COL_SPAN.NAME}>
              <SkeletonLoader variant={SKELETON_LOADER_VARIANT_ONWHITE} />
            </GridCell>
            <GridCell span={COL_SPAN.ACTIONS}>
              <SkeletonLoader variant={SKELETON_LOADER_VARIANT_ONWHITE} />
            </GridCell>
          </>
        )}
      </Grid>
    </DataListRow>
  )
}
